// Walking round a home (walk.ts on a house page): the house as you'd walk
// through it. Outside, the page's own house shows, roof and all. Step in and
// it becomes a dollhouse with the roof and the floors above lifted off (from
// behind), or closed in over you with a ceiling (through your eyes). Each
// floor is drawn with its walls at full height and its stairs and stairwell
// railings (as the "Look inside" cutaway draws them, but uncut), its own
// furniture, and doors that swing open as someone comes to them.
import * as THREE from 'three';
import { buildHouse, buildFloor, levelSpec, levelsOf, stairRect, stairFrame, digIsland, CUT_H, type Flight } from './house.js';
import { island } from './stage.js';
import { mat, finishShadows } from './kit.js';
import { isleShape } from './neighbourhood.js';
import * as Fu from './furnish.js';
import { WalkGrid, BODY_R, STEP as STEP_UP, type Band, type Ground, type Source, type Place } from './walk.js';

export type HouseWalk = ReturnType<typeof houseWalk>;

export function houseWalk(h: Fu.House, lot: THREE.Group) {
  const spec = Fu.withDrapes(h.spec, h.chores?.drapes || 0);
  const [W, D] = h.plot;
  const FL = spec.floorH ?? 0.45, SH = spec.storyH ?? 2.75, T = spec.wallT ?? 0.16;
  const levels = levelsOf(spec), nb = spec.basements || 0;
  const stairs: Flight[] = spec.stairs || [];
  const OUT = levels.length; // the camera view from outside
  const idx = (k: number) => levels.indexOf(k);
  const baseOf = (k: number) => FL + k * SH;

  // the page's own pieces of the lot
  const kids = lot.children;
  const pageIsle = kids.find((c) => c.userData.walkIsle) || null;
  const pageShell = kids.find((c) => c.userData.house) || null;
  const pageFurn = kids.find((c) => c.userData.walkFurniture) || null;
  const rest = kids.filter((c) => c !== pageIsle && c !== pageShell && c !== pageFurn);

  // ours: each floor whole, its ceiling, its furniture, and the island dug for the basements
  // what's on show hangs from `root`; the rest waits in `pool`, out of the scene, so a hidden
  // floor or cutaway costs nothing a frame (a hidden object's matrices are still updated)
  const root = new THREE.Group(); root.name = 'walk';
  const pool = new THREE.Group();
  const put = (o: THREE.Object3D, on: boolean) => { if (on && o.parent !== root) root.add(o); else if (!on && o.parent !== pool) pool.add(o); };
  const stack = new Map<number, THREE.Object3D>(), ceilings = new Map<number, THREE.Object3D>(), furn = new Map<number, THREE.Object3D>();
  // a ceiling lit from the room below (it faces away from the sun), not left in the floor's shadow
  const ceilM = new THREE.MeshStandardMaterial({ color: '#f2ece2', roughness: 0.95, emissive: new THREE.Color('#f2ece2'), emissiveIntensity: 0.32 });
  for (const k of levels) {
    const sub = levelSpec(spec, k);
    if (!sub.masses.length) continue;
    const wells = stairs.filter((st) => st.to === k);
    const g = buildHouse(sub, {
      cutaway: true, upper: k !== 0, closed: false, camDir: new THREE.Vector2(0, 0), cutH: SH,
      flights: stairs.filter((st) => st.from === k), wells, holes: wells.map(stairRect),
    });
    g.name = `floor ${k}`;
    pool.add(g); stack.set(k, g);
    // a ceiling just under the floor above, open where the stairs go up through it
    const c = new THREE.Group();
    const holes = stairs.filter((st) => st.from === k).map(stairRect);
    // (a basement's ceiling is the underside of the ground floor's foundation)
    const y = k === -1 ? -0.012 : baseOf(k) + SH - 0.37;
    for (const m of sub.masses) {
      const x0 = m.x - T / 2, x1 = m.x + m.w + T / 2, z0 = m.y - T / 2, z1 = m.y + m.d + T / 2;
      for (const [a, b, p, q] of cut(x0, x1, z0, z1, holes)) {
        const box = new THREE.Mesh(new THREE.BoxGeometry(b - a, 0.03, q - p), ceilM);
        box.position.set((a + b) / 2, y - 0.015, (p + q) / 2); box.castShadow = false; box.receiveShadow = false;
        c.add(box);
      }
    }
    c.userData.walkSkip = true;
    pool.add(c); ceilings.set(k, c);
    if (k !== 0) { const f = Fu.furniture(h, k); pool.add(f); furn.set(k, f); }
  }
  finishShadows(pool);
  for (const c of ceilings.values()) c.traverse((o: any) => { if (o.isMesh) { o.castShadow = false; o.receiveShadow = false; } });

  // from behind, indoors: the floor you're on as "Look inside" shows it, the walls on the camera's
  // side and between rooms cut low, the floors below closed up; one for each side the camera can be on
  const cuts = new Map<string, THREE.Object3D>();
  const cutFor = (L: number, q: [number, number]) => {
    const key = `${L}|${q[0]}|${q[1]}`;
    let g = cuts.get(key);
    if (!g) {
      g = buildFloor(spec, L, { camDir: new THREE.Vector2(q[0], q[1]) });
      g.name = `cutaway ${key}`;
      finishShadows(g);
      hinge(g);
      pool.add(g); cuts.set(key, g);
    }
    return g;
  };

  // the island: as it is, or (with basements) dug for the stairs down, and cut round a basement in view
  const isles = new Map<string, THREE.Object3D>();
  const isleFor = (key: string, floor: number) => {
    let g = isles.get(key);
    if (!g) {
      const i = island(-0.5, -0.5, W + 0.5, D + 0.5, { stripes: (h.chores?.lawn || 0) === 0, ...isleShape(h.plot_number || 0, h.spec), floaters: true, ...Fu.groundOf(h) });
      g = digIsland(i, spec, floor);
      finishShadows(g);
      pool.add(g); isles.set(key, g);
    }
    return g;
  };

  // ----- doors: a leaf swings in on its hinge as someone comes up to it
  type Door = { pivot: THREE.Group; sign: number; at: [number, number]; y: number; open: number };
  const doors: Door[] = [];
  const hinge = (shell: THREE.Object3D) => {
    const groups = new Map<string, { parent: THREE.Object3D; leaf: any; meshes: THREE.Object3D[] }>();
    shell.traverse((o) => {
      const lf = o.userData.leaf; if (!lf || !o.parent) return;
      const k = o.parent.uuid + lf.key;
      if (!groups.has(k)) groups.set(k, { parent: o.parent, leaf: lf, meshes: [] });
      groups.get(k)!.meshes.push(o);
    });
    for (const { parent, leaf, meshes } of groups.values()) {
      if (parent.userData.walkHinged?.has(leaf.key)) continue;
      const pivot = new THREE.Group();
      const n = leaf.line + leaf.n;
      if (leaf.dir === 'h') pivot.position.set(leaf.u0, 0, n); else pivot.position.set(n, 0, leaf.u0);
      parent.add(pivot); parent.updateMatrixWorld(true);
      for (const m of meshes) { pivot.attach(m); m.traverse((x: any) => { x.castShadow = false; }); }
      (parent.userData.walkHinged ||= new Set()).add(leaf.key);
      const out = leaf.out || 1, mid = (leaf.u0 + leaf.u1) / 2;
      doors.push({ pivot, sign: leaf.dir === 'h' ? out : -out, at: leaf.dir === 'h' ? [mid, n] : [n, mid], y: leaf.y, open: 0 });
    }
  };
  if (pageShell) hinge(pageShell);
  for (const g of stack.values()) hinge(g);

  // ----- what can be walked on, and what's in the way
  const bands: Band[] = levels.map((k) => ({ base: baseOf(k), lo: baseOf(k) - SH / 2 - 0.6, hi: baseOf(k) + SH / 2 + 0.6 }));
  const ground = (spec.masses || []).filter((m: any) => !(m.level > 0));
  const grounds: Ground[] = [{ x0: -0.5, z0: -0.5, x1: W + 0.5, z1: D + 0.5, y: 0,
    // under the house its own floor takes over (so the stairs down open into a stairwell, not onto lawn)
    holes: ground.map((m: any) => ({ x0: m.x - T / 2 - 0.04, z0: m.y - T / 2 - 0.04, x1: m.x + m.w + T / 2 + 0.04, z1: m.y + m.d + T / 2 + 0.04 })) }];
  const insideViews = (from: number) => levels.map((_, i) => i).filter((i) => levels[i] >= from);
  // indoors the camera sees the cutaway, whose walls stand no higher than CUT_H (the furniture as it is)
  const cap = (vw: number) => (vw === OUT ? Infinity : baseOf(levels[vw]) + SH + 0.05);
  const capWalls = (vw: number) => (vw === OUT ? Infinity : baseOf(levels[vw]) + CUT_H + 0.05);
  const sources: Source[] = [];
  // a floor is stood on at about its own height (a sill, a porch, a step or two); higher or lower only on a
  // flight of stairs. Without this the flat top of a doorway's head (2.4 m up, inside a floor's band) was
  // taken for the floor there, a wall's height over the sill, and closed every doorway, outside and in
  const flights = stairs.map(stairRect).map(([x0, z0, x1, z1]) => ({ x0: x0 - 0.12, z0: z0 - 0.12, x1: x1 + 0.12, z1: z1 + 0.12 }));
  // over a flight itself, only its treads are stood on: what's well above or below the slope there (a
  // handrail's top, the well's railing, the trim along the wall under the floor above, the floor above's
  // edge) is in the way, as it is on the flight's own floor. Counted as floor on the floor above, a rail
  // a metre over the steps was a step too high to take, so halfway up, where the floor changes, the
  // figure was suddenly standing in something and couldn't move at all (and the stairs were narrower
  // going down than up)
  const slopes = stairs.map((st) => {
    const [x0, z0, x1, z1] = stairRect(st), f = stairFrame(st), y0 = baseOf(st.from), y1 = baseOf(st.to);
    // s up the flight from its foot edge, for a point (x, z)
    const sAt = (x: number, z: number) => (x - f.at(0, 0)[0]) * f.d[0] + (z - f.at(0, 0)[1]) * f.d[1];
    return { x0, z0, x1, z1, y: (x: number, z: number) => y0 + (Math.max(0, Math.min(st.len, sAt(x, z))) / st.len) * (y1 - y0) };
  });
  const stand = (b: number, x: number, z: number, y: number) => {
    const on = slopes.find((r) => x > r.x0 && x < r.x1 && z > r.z0 && z < r.z1);
    // (or, well under the flight's high end, the floor beneath it: a cupboard's worth of room)
    if (on) { const s = on.y(x, z); return Math.abs(y - s) <= 0.4 || (Math.abs(y - bands[b].base) <= STEP_UP + 0.05 && s - y > 2.2); }
    return Math.abs(y - bands[b].base) <= STEP_UP + 0.05 || flights.some((r) => x > r.x0 && x < r.x1 && z > r.z0 && z < r.z1);
  };
  const onFlight = (x: number, z: number) => flights.some((r) => x > r.x0 + 0.18 && x < r.x1 - 0.18 && z > r.z0 + 0.18 && z < r.z1 - 0.18);
  for (const [k, g] of stack) sources.push({ obj: g, kind: 'support', views: insideViews(k), cap: capWalls, stand, soft: onFlight });
  // the house from outside (its porch posts and railings, its eaves) stops you only outside its walls:
  // indoors its floors are drawn without stairwells, and the floors themselves are what count. Over the
  // foundation (the walls, the sills, the stone it stands on) the ground floor's own walls stop you, and its
  // foundation and sills are steps: the page's house there would make the foundation's face a wall and
  // close every doorway that has no porch
  // (on a floor above, the same goes for every wing that has that floor: a lower wing's roof, poking in
  // under the walls of the one on top of it, is no step)
  const massesOn = levels.map((k) => (spec.masses || []).filter((m: any) => (m.level || 0) <= k && k < (m.level || 0) + (m.stories || 1) || (k < 0 && !(m.level > 0))));
  const within = (b: number, x: number, z: number) => (massesOn[b] || []).some((m: any) => x > m.x - T / 2 - 0.06 && x < m.x + m.w + T / 2 + 0.06 && z > m.y - T / 2 - 0.06 && z < m.y + m.d + T / 2 + 0.06);
  if (pageShell) sources.push({ obj: pageShell, kind: 'solid', views: [OUT], only: (x, z, b) => !within(b, x, z) });
  // from outside, the camera keeps out of the house as well as off its walls: stepping out of the door
  // with the camera behind you, it rises over the eaves or comes in close rather than look out from the hall
  // (a block for each wing, never drawn, that stops nobody)
  const camBlock = new THREE.Group();
  for (const m of spec.masses || []) {
    const y0 = m.level > 0 ? baseOf(m.level) : 0, y1 = baseOf((m.level || 0) + (m.stories || 1));
    const box = new THREE.Mesh(new THREE.BoxGeometry(m.w + T, y1 - y0, m.d + T), new THREE.MeshBasicMaterial());
    box.position.set(m.x + m.w / 2, (y0 + y1) / 2, m.y + m.d / 2);
    camBlock.add(box);
  }
  sources.push({ obj: camBlock, kind: 'solid', views: [OUT], only: () => false });
  // a stoop at each outside door with no porch to step onto: a stone block half the sill's height
  const stoops = stoopsFor(spec, ground, FL, T);
  if (stoops) { root.add(stoops); sources.push({ obj: stoops, kind: 'support', views: [OUT] }); }
  // a doorway is kept clear: furniture set down in one (a bed against a door, a plant in an arch) is
  // brushed past rather than closing a room off
  const ways = doorways(spec);
  const clear = (x: number, z: number, b: number) => !ways.some((r) => r.level === levels[b] && x > r.x0 && x < r.x1 && z > r.z0 && z < r.z1);
  if (pageFurn) sources.push({ obj: pageFurn, kind: 'solid', views: [...insideViews(0), OUT], only: clear, soft: true });
  for (const [k, f] of furn) sources.push({ obj: f, kind: 'solid', views: insideViews(k), only: clear, soft: true });
  for (const r of rest) sources.push({ obj: r, kind: 'solid', views: [...insideViews(0), OUT] });

  const inside = (k: number, x: number, z: number) => k !== 0 || ground.some((m: any) => x > m.x && x < m.x + m.w && z > m.y && z < m.y + m.d);

  // everything that can show, built and its shaders compiled now, while the page says "Finding your
  // feet": nothing is built or compiled for the first time mid-stride (a floor, a cutaway, a ceiling)
  const ready = async (prep: { renderer: THREE.WebGLRenderer; cam: THREE.Camera; scene: THREE.Scene }) => {
    const breathe = () => new Promise((r) => setTimeout(r, 0));
    for (const k of levels) for (const q of [[1, 1], [1, -1], [-1, 1], [-1, -1]] as [number, number][]) { cutFor(k, q); await breathe(); }
    if (nb) { isleFor('up', 0); for (const k of levels) if (k < 0) isleFor(`cut${k}`, k); }
    for (const g of [pool, root]) {
      g.traverse((o: any) => {
        for (const m of Array.isArray(o.material) ? o.material : o.material ? [o.material] : [])
          for (const t of ['map', 'normalMap', 'roughnessMap', 'bumpMap', 'emissiveMap']) if (m[t]) prep.renderer.initTexture(m[t]);
      });
      try { await (prep.renderer as any).compileAsync(g, prep.cam, prep.scene); } catch { /* then it compiles as it's first drawn */ }
    }
  };
  const build = async (prep?: { renderer: THREE.WebGLRenderer; cam: THREE.Camera; scene: THREE.Scene }) => {
    lot.updateMatrixWorld(true); root.updateMatrixWorld(true);
    const grid = new WalkGrid({ x0: -1.2, z0: -1.2, x1: W + 1.2, z1: D + 1.2 }, 0.08, bands, levels.length + 1, [...levels.map(baseOf), 0]);
    await grid.build(grounds, sources);
    if (prep) await ready(prep);
    return grid;
  };

  // where to start: on the path in front of the door, facing it
  const door = Fu.frontDoor(spec) || [W / 2, D - 3];
  const startAt = (grid: WalkGrid): { x: number; z: number; level: number; yaw: number } => {
    const b0 = idx(0);
    for (let r = 0; r < 4; r += 0.2) for (let a = 0; a < Math.PI * 2; a += Math.PI / 8) {
      const x = door[0] + Math.sin(a) * r, z = door[1] + 2.3 + Math.cos(a) * r;
      if (grid.free(b0, x, z, grid.ground(b0, x, z))) return { x, z, level: 0, yaw: Math.PI };
    }
    return { x: door[0], z: Math.min(D - 0.4, door[1] + 2.3), level: 0, yaw: Math.PI };
  };
  // somewhere on a flight of stairs: frac 0 at its foot, 1 at its top
  const onStairs = (i: number, frac: number) => {
    const st = stairs[i]; if (!st) return null;
    const f = stairFrame(st), [x, z] = f.at(Math.max(0.15, Math.min(st.len - 0.15, frac * st.len)), 0.5);
    return { x, z, level: frac < 0.5 ? st.from : st.to, yaw: Math.atan2(f.d[0], f.d[1]) };
  };

  // ----- what shows, for where you are
  let placedKey = '';
  const apply = (p: Place) => {
    const L = p.level, first = p.first, inn = p.inside, third = inn && !first;
    const key = `${L}|${first}|${inn}|${third ? p.quad.join(',') : ''}`;
    if (key === placedKey) return false;
    placedKey = key;
    if (pageShell) pageShell.visible = !inn;
    // through your eyes: every floor whole, up to the one above (seen up the stairs), with ceilings
    for (const [k, g] of stack) put(g, inn && first && k <= L + 1);
    for (const [k, c] of ceilings) put(c, inn && first && k <= L + 1);
    // from behind: the cutaway of your floor
    const cut = third ? cutFor(L, p.quad) : null;
    for (const g of cuts.values()) put(g, g === cut);
    for (const [k, f] of furn) put(f, inn && (first ? k <= L + 1 : k <= L));
    const under = L < 0 && !first;
    if (pageFurn) pageFurn.visible = !under;
    for (const r of rest) r.visible = !under;
    if (nb) {
      // the island: dug down the stairwell (above ground), cut round the basement (from behind,
      // below ground), or not at all (through your eyes below ground, you're inside it)
      if (pageIsle) pageIsle.visible = false;
      const isle = first && L < 0 ? null : isleFor(under ? `cut${L}` : 'up', under ? L : 0);
      for (const g of isles.values()) put(g, g === isle);
    }
    return true;
  };
  // doors open for whoever comes to them (you, or a plazmi on its way)
  const tick = (dt: number, who: { x: number; z: number; y: number }[]) => {
    for (const d of doors) {
      const near = who.some((w) => Math.abs(w.y - d.y) < 1.4 && Math.hypot(w.x - d.at[0], w.z - d.at[1]) < 1.5);
      d.open += ((near ? 1 : 0) - d.open) * (1 - Math.exp(-dt * (near ? 5 : 2)));
      d.pivot.rotation.y = d.sign * d.open * 1.65;
    }
  };
  const dispose = () => {
    root.removeFromParent();
    if (pageShell) pageShell.visible = true;
    if (pageFurn) pageFurn.visible = true;
    if (pageIsle) pageIsle.visible = true;
    for (const r of rest) r.visible = true;
    for (const d of doors) d.pivot.rotation.y = 0;
    for (const g of [root, pool]) g.traverse((o: any) => o.geometry?.dispose?.());
  };
  return { root, levels, bands, build, inside, viewOf: (band: number, inn: boolean) => (inn ? band : OUT), apply, tick, dispose, startAt, onStairs, stairs, BODY_R };
}

// the rectangle less the stairwells: strips round each hole
function cut(x0: number, x1: number, z0: number, z1: number, holes: [number, number, number, number][]) {
  const xs = [...new Set([x0, x1, ...holes.flatMap(([a, , b]) => [a, b]).filter((v) => v > x0 && v < x1)])].sort((a, b) => a - b);
  const out: [number, number, number, number][] = [];
  for (let i = 0; i < xs.length - 1; i++) {
    const a = xs[i], b = xs[i + 1], m = (a + b) / 2;
    const hs = holes.filter(([ha, , hb]) => ha < m && hb > m).map(([, hz0, , hz1]) => [Math.max(z0, hz0), Math.min(z1, hz1)]).sort((p, q) => p[0] - q[0]);
    let z = z0;
    for (const [p, q] of hs) { if (p > z) out.push([a, b, z, p]); z = Math.max(z, q); }
    if (z < z1) out.push([a, b, z, z1]);
  }
  return out;
}

// The outside doors on the ground floor that open onto lawn (not onto a porch's deck), each with a stoop:
// a stone block, half the sill's height, so the step up from the path comes in two
function stoopsFor(spec: any, ground: any[], FL: number, T: number) {
  const inMass = (x: number, z: number) => ground.some((m: any) => x > m.x && x < m.x + m.w && z > m.y && z < m.y + m.d);
  const p = spec.porch;
  const onPorch = (x: number, z: number) => !!p && x > p.x0 - 0.1 && x < p.x1 + 0.1 && z > p.z - 0.1 && z < p.z + p.depth + 0.4;
  const g = new THREE.Group(); g.name = 'stoops';
  const stone = mat(spec.exterior?.foundation?.color || '#b9ae9c', { rough: 0.95 });
  const top = mat('#c9c2b4', { rough: 0.9 });
  const h = Math.max(0.12, FL / 2), deep = 0.46, wide = 1.2, face = T / 2 + 0.035;
  for (const o of spec.openings || []) {
    if ((o.kind !== 'door' && o.kind !== 'french') || o.level > 0) continue;
    const [d, a, b] = String(o.e).split(':'); const u = +a, v = +b;
    const mid: [number, number] = d === 'h' ? [u + 0.5, v] : [u, v + 0.5], n: [number, number] = d === 'h' ? [0, 1] : [1, 0];
    const A = inMass(mid[0] + n[0] * 0.5, mid[1] + n[1] * 0.5), B = inMass(mid[0] - n[0] * 0.5, mid[1] - n[1] * 0.5);
    if (A === B) continue; // a door between rooms
    const s = A ? -1 : 1; // which way is out
    const ox = mid[0] + n[0] * s * (face + deep / 2), oz = mid[1] + n[1] * s * (face + deep / 2);
    if (onPorch(mid[0] + n[0] * s * 0.6, mid[1] + n[1] * s * 0.6)) continue;
    const [sx, sz] = d === 'h' ? [wide, deep] : [deep, wide];
    const box = new THREE.Mesh(new THREE.BoxGeometry(sx, h, sz), [stone, stone, top, stone, stone, stone]);
    box.position.set(ox, h / 2, oz);
    box.castShadow = true; box.receiveShadow = true;
    g.add(box);
  }
  return g.children.length ? g : null;
}

// Every doorway and arch, floor by floor, with room either side of it to come and go
function doorways(spec: any) {
  const all = [...(spec.openings || []).map((o: any) => ({ o, level: o.level || 0 })),
    ...(spec.masses || []).flatMap((m: any) => (m.openings || []).map((o: any) => ({ o, level: o.level ?? m.level ?? 0 })))];
  const out: { level: number; x0: number; z0: number; x1: number; z1: number }[] = [];
  for (const { o, level } of all) {
    if (o.kind !== 'door' && o.kind !== 'french' && o.kind !== 'arch') continue;
    const [d, a, b] = String(o.e).split(':'); const u = +a, v = +b, deep = 1.0;
    if (d === 'h') out.push({ level, x0: u + 0.04, x1: u + 0.96, z0: v - deep, z1: v + deep });
    else out.push({ level, x0: u - deep, x1: u + deep, z0: v + 0.04, z1: v + 0.96 });
  }
  return out;
}
